import { useState, useEffect, useCallback } from 'react';
import { tarjetaControlApi } from '../../lib/api';
import { useRegisterCmdKCommands } from '../../contexts/CmdKContext';
import { Card, CardContent } from '../../components/ui/card';
import { TableSkeleton } from '../../lib/skeletons';
import { toast } from '../../hooks/use-toast';
import { Users, AlertTriangle, CalendarDays, Download, Flag, Filter, Printer } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { triggerPrint } from '../../lib/printUtils';

const TIPO_LABELS = { P: 'Permiso', L: 'Licencia', V: 'Vacaciones', F: 'Falla', O: 'Otro', FE: 'Feriado', X: 'No Ingresado (llena atrás)', Q: 'Finiquito (llena adelante)', T: 'Trato' };
const TIPO_OPTIONS = ['', 'P', 'L', 'V', 'F', 'O', 'T', 'FE', 'X', 'Q'];

const CELL_STYLES = {
  A: 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300',
  '*': 'bg-muted/40 text-muted-foreground',
  X: 'bg-slate-300/40 text-slate-500 dark:bg-slate-700/40 dark:text-slate-400',
  Q: 'bg-stone-400/30 text-stone-600 dark:bg-stone-600/30 dark:text-stone-400',
  T: 'bg-yellow-500/20 text-yellow-700 dark:text-yellow-300',
  P: 'bg-blue-500/20 text-blue-700 dark:text-blue-300',
  L: 'bg-purple-500/20 text-purple-700 dark:text-purple-300',
  V: 'bg-cyan-500/20 text-cyan-700 dark:text-cyan-300',
  F: 'bg-red-500/20 text-red-700 dark:text-red-300',
  O: 'bg-orange-500/20 text-orange-700 dark:text-orange-300',
  FE: 'bg-rose-500/20 text-rose-700 dark:text-rose-300',
};

function getDefaultMes() {
  const now = new Date();
  let m = now.getMonth();
  let y = now.getFullYear();
  if (m === 0) { m = 12; y -= 1; }
  return `${String(m).padStart(2, '0')}/${String(y).slice(-2)}`;
}

function mesDisplayLabel(mes) {
  if (!mes) return '';
  const [mm, yy] = mes.split('/');
  const months = ['', 'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
  return `${months[parseInt(mm)] || mm}/20${yy}`;
}

function CellEditor({ cell, onSave }) {
  const [open, setOpen] = useState(false);
  const val = cell.val;
  if (val === 'X' || val === 'Q') {
    return <span className={`inline-block w-6 h-6 leading-6 rounded text-[10px] font-bold ${CELL_STYLES[val] || CELL_STYLES.X}`}>{val}</span>;
  }
  const style = CELL_STYLES[val] || CELL_STYLES['*'];
  const hasHex = cell.hex > 0;
  const hasAtraso = cell.atraso > 0;
  const isMissing = cell.is_missing;

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={`inline-flex items-center justify-center w-6 h-6 rounded text-[10px] font-bold transition-all cursor-pointer hover:ring-2 hover:ring-primary/40 ${style} ${hasHex ? 'ring-1 ring-amber-400' : ''} ${hasAtraso ? 'ring-1 ring-red-400' : ''} ${isMissing ? 'ring-2 ring-orange-500 animate-pulse' : ''}`}
        title={`${val}${hasHex ? ` | HEX:${cell.hex}h` : ''}${hasAtraso ? ` | Atraso:${cell.atraso}min` : ''}${cell.hh_total ? ` | HH:${cell.hh_total}` : ''}${isMissing ? ' | DIA LABORABLE SIN INFO' : ''}`}
        data-testid={`cell-${cell.dia}`}
      >
        {val}
        {hasHex && <span className="absolute -top-1 -right-1 w-2 h-2 bg-amber-400 rounded-full" />}
        {hasAtraso && !hasHex && <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-400 rounded-full" />}
      </button>
      {open && (
        <div className="absolute z-50 top-7 left-0 bg-popover border rounded-lg shadow-lg p-1 min-w-[90px]" data-testid={`cell-editor-${cell.dia}`}>
          {TIPO_OPTIONS.map(t => (
            <button
              key={t || 'clear'}
              onClick={() => { onSave(t); setOpen(false); }}
              className={`block w-full text-left px-2 py-1 text-xs rounded hover:bg-muted transition-colors ${val === t ? 'font-bold bg-muted' : ''}`}
            >
              {t ? `${t} - ${TIPO_LABELS[t]}` : 'Limpiar'}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AsistenciaMensualPage() {
  const [mes, setMes] = useState(getDefaultMes);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [autoMarcando, setAutoMarcando] = useState(false);
  // Iter 236.q (Carlos): filtro de trabajadores
  const [filtroTrabajadores, setFiltroTrabajadores] = useState([]); // array de trabajador_numero
  const [searchTrab, setSearchTrab] = useState('');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await tarjetaControlApi.getAsistenciaMensual({ mes });
      setData(res.data);
    } catch {
      toast({ title: 'Error al cargar asistencia', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [mes]);

  useEffect(() => { loadData(); }, [loadData]);

  // Helpers navegación de mes ("MM/AA")
  const shiftMes = useCallback((delta) => {
    const [mm, yy] = mes.split('/').map((x) => parseInt(x, 10));
    let m = mm + delta;
    let y = 2000 + yy;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    setMes(`${String(m).padStart(2, '0')}/${String(y % 100).padStart(2, '0')}`);
  }, [mes]);

  // Cmd+K: comandos contextuales
  useRegisterCmdKCommands(
    'asistencia-mensual',
    `Asistencia · ${mesDisplayLabel(mes)}`,
    [
      {
        id: 'asist-prev-month',
        label: 'Ir al mes anterior',
        sublabel: 'Navegar hacia atrás',
        icon: 'ArrowLeft',
        keywords: 'mes anterior previo atras',
        run: () => shiftMes(-1),
      },
      {
        id: 'asist-next-month',
        label: 'Ir al mes siguiente',
        sublabel: 'Navegar hacia adelante',
        icon: 'ArrowRight',
        keywords: 'mes siguiente proximo adelante',
        run: () => shiftMes(1),
      },
      {
        id: 'asist-hoy',
        label: 'Ir al mes actual',
        icon: 'Calendar',
        keywords: 'hoy mes actual',
        run: () => setMes(getDefaultMes()),
        enabled: mes !== getDefaultMes(),
      },
      {
        id: 'asist-auto-feriados',
        label: `Marcar feriados oficiales de ${mesDisplayLabel(mes)}`,
        sublabel: 'api.boostr.cl',
        icon: 'Flag',
        keywords: 'feriados festivos boostr auto marcar',
        run: () => handleAutoMarcarFeriados(),
      },
      {
        id: 'asist-refresh',
        label: 'Refrescar asistencia',
        icon: 'RefreshCw',
        run: () => loadData(),
      },
    ],
    [mes, shiftMes, loadData],
  );

  const handleClasificar = async (trabajador_numero, dia, tipo) => {
    try {
      await tarjetaControlApi.clasificarDia({ trabajador_numero, mes, dia, tipo });
      loadData();
    } catch {
      toast({ title: 'Error al clasificar', variant: 'destructive' });
    }
  };

  const handleToggleFeriado = async (dia) => {
    try {
      await tarjetaControlApi.toggleFeriado({ mes, dia });
      loadData();
    } catch {
      toast({ title: 'Error al cambiar feriado', variant: 'destructive' });
    }
  };

  const handleAutoMarcarFeriados = async () => {
    if (!window.confirm(`¿Marcar automáticamente los feriados chilenos de ${mesDisplayLabel(mes)}?\n\nSe consultará api.boostr.cl y se mergeará con los feriados manuales existentes (no se borrarán).`)) return;
    try {
      setAutoMarcando(true);
      const res = await tarjetaControlApi.autoMarcarFeriados(mes);
      const { marcados, feriados_aplicados } = res.data;
      if (marcados === 0) {
        toast({ title: 'Sin feriados oficiales en este mes', description: 'No se encontraron feriados desde api.boostr.cl' });
      } else {
        const detalle = feriados_aplicados.map(f => `Día ${f.dia}: ${f.nombre}`).join('\n');
        toast({
          title: `${marcados} feriado${marcados !== 1 ? 's' : ''} marcado${marcados !== 1 ? 's' : ''} automáticamente`,
          description: detalle,
        });
      }
      await loadData();
    } catch (err) {
      toast({ title: err?.response?.data?.detail || 'Error al consultar feriados', variant: 'destructive' });
    } finally {
      setAutoMarcando(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      setExporting(true);
      const res = await tarjetaControlApi.exportAsistenciaExcel(mes);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Asistencia_${mesDisplayLabel(mes).replace('/', '_')}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast({ title: 'Excel descargado' });
    } catch {
      toast({ title: 'Error al exportar', variant: 'destructive' });
    } finally {
      setExporting(false);
    }
  };

  const handleMesChange = (e) => {
    const val = e.target.value;
    if (!val) return;
    const [y, m] = val.split('-');
    setMes(`${m}/${y.slice(-2)}`);
  };

  // Carlos (10/05/2026): imprimir matriz de asistencia. Genera HTML con la
  // grilla mes×trabajador igual que en pantalla.
  const imprimirAsistencia = () => {
    const d = data;
    if (!d?.grid?.length) return;
    const days = d.dias || [];
    const cellLabel = (cell) => {
      if (!cell) return '';
      const v = cell.val;
      if (!v && !cell.atraso && !cell.hex) return '';
      const parts = [];
      if (v) parts.push(v);
      if (cell.atraso) parts.push(`-${cell.atraso}`);
      if (cell.hex) parts.push(`H${cell.hex}`);
      return parts.join('');
    };
    const cellBg = (cell) => {
      if (!cell?.val) return '';
      const map = { A: '#a7f3d0', P: '#bfdbfe', L: '#e9d5ff', V: '#a5f3fc', F: '#fecaca', O: '#fed7aa', T: '#fef08a', X: '#cbd5e1', Q: '#d6d3d1', FE: '#fda4af', '*': '#e5e7eb' };
      return map[cell.val] || '';
    };
    const escapeHtml = (s) => String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    const headerRow = days.map((day) => {
      const isWeekend = day.dow === 0 || day.dow === 6;
      return `<th style="border:1px solid #cbd5e1;padding:2px;background:${isWeekend ? '#fef3c7' : '#f1f5f9'};font-size:7px;width:18px;">${day.dia}</th>`;
    }).join('');

    const dataRows = d.grid.map((row) => {
      const cells = days.map((day) => {
        const cell = row.cells?.[day.dia] || {};
        const bg = cellBg(cell);
        const lbl = cellLabel(cell);
        return `<td style="border:1px solid #cbd5e1;padding:2px;text-align:center;font-size:7px;background:${bg};">${escapeHtml(lbl)}</td>`;
      }).join('');
      return `<tr>
        <td style="border:1px solid #cbd5e1;padding:3px 5px;font-size:8px;font-weight:600;white-space:nowrap;">${escapeHtml(row.trabajador_nombre || row.rut || '')}</td>
        ${cells}
        <td style="border:1px solid #cbd5e1;padding:3px;text-align:right;font-family:monospace;font-size:8px;">${row.totales?.A || 0}</td>
        <td style="border:1px solid #cbd5e1;padding:3px;text-align:right;font-family:monospace;font-size:8px;">${row.totales?.atraso_total || 0}</td>
      </tr>`;
    }).join('');

    const html = `<!DOCTYPE html><html lang="es"><head><meta charset="utf-8"/>
<title>Asistencia ${mesDisplayLabel(mes)} - CHIRINO STEEL</title>
<style>
  @page { size: letter landscape; margin: 8mm; }
  body { font-family: Arial,sans-serif; font-size:10px; margin:0; padding:5px; color:#1a202c; }
  .header { text-align:center; border-bottom:2px solid #1a365d; padding-bottom:4px; margin-bottom:6px; }
  .header h1 { font-size:14px; color:#1a365d; margin:0 0 2px; }
  .header h2 { font-size:11px; color:#374151; margin:0; }
  table { width:100%; border-collapse:collapse; }
  th { background:#1a365d; color:#fff; padding:2px; border:1px solid #1a365d; font-weight:600; text-transform:uppercase; }
  .footer { text-align:center; font-size:7px; color:#94a3b8; margin-top:6px; padding-top:3px; border-top:1px solid #e2e8f0; }
  .toolbar { background:#1e3a8a; color:#fff; padding:5px 10px; display:flex; justify-content:space-between; align-items:center; margin-bottom:5px; border-radius:3px; }
  .toolbar button { background:#fff; color:#1e3a8a; border:0; padding:3px 10px; border-radius:3px; font-weight:600; cursor:pointer; margin-left:5px; }
  @media print { .toolbar { display:none !important; } body { padding:0; } }
</style></head>
<body>
  <div class="toolbar"><span><b>CHIRINO STEEL</b> · Asistencia ${mesDisplayLabel(mes)}</span><span><button onclick="window.print()">🖨️ Imprimir</button><button onclick="window.close()">✕ Cerrar</button></span></div>
  <div class="header">
    <h1>CHIRINO STEEL ING. Y CONT. SPA</h1>
    <h2>ASISTENCIA MENSUAL · ${mesDisplayLabel(mes)} · ${d.grid.length} trabajadores</h2>
  </div>
  <table>
    <thead><tr>
      <th style="text-align:left;padding:3px 5px;font-size:8px;">Trabajador</th>
      ${headerRow}
      <th style="font-size:7px;">As.</th>
      <th style="font-size:7px;">Atr.</th>
    </tr></thead>
    <tbody>${dataRows}</tbody>
  </table>
  <div class="footer">Generado ${new Date().toLocaleString('es-CL')} · Letras: A=Asistió · P=Permiso · L=Licencia · V=Vacaciones · F=Falla · FE=Feriado · X=No Ingresado · Q=Finiquito · T=Trato · O=Otro</div>
</body></html>`;
    // triggerPrint YA dispara la impresión (evita doble diálogo). Carlos, Jun 2026.
    triggerPrint(html);
  };

  const mesInputVal = (() => {
    const [mm, yy] = mes.split('/');
    return `20${yy}-${mm}`;
  })();

  if (loading && !data) return <TableSkeleton rows={15} cols={20} />;

  const d = data || { days_in_month: 30, grid: [], resumen_ausencias: {}, day_info: [], feriados: [] };
  const ra = d.resumen_ausencias || {};
  const totalAusencias = (ra.P || 0) + (ra.L || 0) + (ra.V || 0) + (ra.F || 0) + (ra.O || 0) + (ra.FE || 0);
  const dayInfo = d.day_info || [];
  const feriados = new Set(d.feriados || []);

  // Iter 236.q (Carlos): grid filtrado por trabajadores seleccionados
  const filtroSet = new Set(filtroTrabajadores);
  const filteredGrid = filtroSet.size > 0
    ? (d.grid || []).filter((r) => filtroSet.has(r.trabajador_numero))
    : (d.grid || []);
  const dForRender = { ...d, grid: filteredGrid };
  const trabSearchLower = (searchTrab || '').toLowerCase();
  const trabajadoresDisponibles = (d.grid || []).filter((r) => {
    if (!trabSearchLower) return true;
    const txt = `${r.trabajador_nombre || ''} ${r.rut || ''} ${r.trabajador_numero || ''}`.toLowerCase();
    return txt.includes(trabSearchLower);
  });

  return (
    <div className="space-y-4 page-enter" data-testid="asistencia-mensual">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Asistencia Mensual</h1>
          <p className="text-sm text-muted-foreground">Planilla {mesDisplayLabel(mes)} — Click n° día para feriado · Click celda para clasificar</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleAutoMarcarFeriados}
            disabled={autoMarcando}
            className="rounded-xl gap-1.5 border-rose-300 text-rose-700 hover:bg-rose-50"
            data-testid="btn-auto-feriados"
            title="Consulta api.boostr.cl y marca los feriados oficiales del mes"
          >
            <Flag className="h-3.5 w-3.5" />
            {autoMarcando ? 'Consultando...' : 'Marcar feriados auto'}
          </Button>
          <Button variant="outline" size="sm" onClick={() => imprimirAsistencia()} disabled={d.grid.length === 0} className="rounded-xl gap-1.5" data-testid="btn-print-asistencia">
            <Printer className="h-3.5 w-3.5" /> Imprimir
          </Button>
          <Button variant="outline" size="sm" onClick={handleExportExcel} disabled={exporting || d.grid.length === 0}
            className="rounded-xl gap-1.5" data-testid="btn-excel-asistencia">
            <Download className="h-3.5 w-3.5" /> {exporting ? 'Exportando...' : 'Excel'}
          </Button>
          <input type="month" value={mesInputVal} onChange={handleMesChange}
            className="h-9 rounded-xl border bg-background px-3 text-sm" data-testid="input-mes" />
        </div>
      </div>

      {/* Warning mes futuro */}
      {d.warning && (
        <Card className="border-amber-400 bg-amber-50/60 dark:bg-amber-950/30" data-testid="banner-mes-futuro">
          <CardContent className="p-3 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0" />
            <p className="text-sm text-amber-700 dark:text-amber-300">{d.warning}</p>
          </CardContent>
        </Card>
      )}

      {/* Iter 236.q (Carlos): filtro trabajadores */}
      <Card className="border-dashed">
        <CardContent className="p-3 space-y-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2 text-sm">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">Filtro trabajadores:</span>
              {filtroTrabajadores.length === 0 ? (
                <span className="text-muted-foreground italic">mostrando todos ({d.grid.length})</span>
              ) : (
                <span className="text-blue-700 font-semibold">{filtroTrabajadores.length} seleccionado(s) — {filteredGrid.length} en grilla</span>
              )}
            </div>
            {filtroTrabajadores.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setFiltroTrabajadores([]); setSearchTrab(''); }}
                className="h-7 text-xs"
                data-testid="btn-limpiar-filtro-trabajadores"
              >Limpiar filtro</Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <input
              type="text"
              placeholder="Buscar por nombre, RUT o número de trabajador…"
              value={searchTrab}
              onChange={(e) => setSearchTrab(e.target.value)}
              className="h-8 rounded-md border bg-background px-3 text-sm flex-1 max-w-md"
              data-testid="input-search-trabajadores"
            />
          </div>
          {(searchTrab || filtroTrabajadores.length > 0) && (
            <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto pt-1 border-t" data-testid="lista-trabajadores-filtro">
              {trabajadoresDisponibles.slice(0, 60).map((t) => {
                const sel = filtroSet.has(t.trabajador_numero);
                return (
                  <button
                    key={t.trabajador_numero}
                    onClick={() => {
                      if (sel) setFiltroTrabajadores(filtroTrabajadores.filter((n) => n !== t.trabajador_numero));
                      else setFiltroTrabajadores([...filtroTrabajadores, t.trabajador_numero]);
                    }}
                    className={`text-[11px] px-2 py-0.5 rounded-full border transition-colors ${sel ? 'bg-blue-600 text-white border-blue-700' : 'bg-background hover:bg-muted border-input'}`}
                    data-testid={`chip-trab-${t.trabajador_numero}`}
                  >
                    {t.nombre || t.trabajador_nombre} {sel && '✓'}
                  </button>
                );
              })}
              {trabajadoresDisponibles.length > 60 && (
                <span className="text-[11px] text-muted-foreground px-2 self-center">… y {trabajadoresDisponibles.length - 60} más, refiná búsqueda</span>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* KPIs */}
      <div className="grid grid-cols-3 md:grid-cols-7 gap-2">
        <Card><CardContent className="p-2.5 flex items-center gap-2">
          <div className="p-1 rounded-lg bg-primary/10"><Users className="h-3.5 w-3.5 text-primary" /></div>
          <div><p className="text-[9px] text-muted-foreground">Trabajadores</p><p className="text-sm font-bold">{d.total_trabajadores}</p></div>
        </CardContent></Card>
        <Card><CardContent className="p-2.5 flex items-center gap-2">
          <div className="p-1 rounded-lg bg-red-500/10"><AlertTriangle className="h-3.5 w-3.5 text-red-500" /></div>
          <div><p className="text-[9px] text-muted-foreground">Ausencias</p><p className="text-sm font-bold">{totalAusencias}</p></div>
        </CardContent></Card>
        {['P', 'L', 'V', 'F', 'FE'].map(t => (
          <Card key={t}><CardContent className="p-2.5 flex items-center gap-2">
            <div className={`p-1 rounded-lg ${CELL_STYLES[t]?.split(' ')[0]}`}>
              <span className={`text-[10px] font-bold ${CELL_STYLES[t]?.split(' ').slice(1).join(' ')}`}>{t}</span>
            </div>
            <div><p className="text-[9px] text-muted-foreground">{TIPO_LABELS[t]}</p><p className="text-sm font-bold">{ra[t] || 0}</p></div>
          </CardContent></Card>
        ))}
      </div>

      {/* Grid */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse" data-testid="asistencia-grid">
              <thead>
                <tr className="bg-muted/50">
                  <th className="sticky left-0 bg-muted/80 backdrop-blur z-20 px-2 py-1.5 text-left font-medium min-w-[130px] border-r">Nombre</th>
                  <th className="px-1 py-1.5 text-center font-medium min-w-[36px]">Ficha</th>
                  <th className="px-1 py-1.5 text-left font-medium min-w-[85px]">Rut</th>
                  <th className="px-1 py-1.5 text-left font-medium min-w-[75px] border-r">Cargo</th>
                  {dayInfo.map((di) => {
                    const isFeriado = feriados.has(di.dia);
                    const bgClass = isFeriado ? 'bg-rose-200 dark:bg-rose-800/40 text-rose-700 dark:text-rose-300'
                      : di.is_sun ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                      : di.is_sat ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
                      : '';
                    return (
                      <th key={di.dia}
                        className={`px-0 py-1.5 text-center font-medium w-7 min-w-[28px] cursor-pointer hover:ring-2 hover:ring-rose-400 transition-all select-none ${bgClass}`}
                        onClick={() => handleToggleFeriado(di.dia)}
                        title={`Día ${di.dia}${isFeriado ? ' (FERIADO) - Click para quitar' : di.is_sat ? ' (Sáb)' : di.is_sun ? ' (Dom)' : ' - Click para marcar feriado'}`}
                        data-testid={`header-day-${di.dia}`}
                      >
                        {di.dia}
                      </th>
                    );
                  })}
                  <th className="px-1 py-1.5 text-center font-bold border-l min-w-[28px]" title="Días con HH">Pres</th>
                  <th className="px-1 py-1.5 text-center font-bold min-w-[22px] text-blue-600">P</th>
                  <th className="px-1 py-1.5 text-center font-bold min-w-[22px] text-purple-600">L</th>
                  <th className="px-1 py-1.5 text-center font-bold min-w-[22px] text-red-600">F</th>
                  <th className="px-1 py-1.5 text-center font-bold min-w-[22px] text-cyan-600">V</th>
                  <th className="px-1 py-1.5 text-center font-bold min-w-[22px] text-yellow-700" title="Trato (descuenta)">T</th>
                  <th className="px-1 py-1.5 text-center font-bold min-w-[22px] text-rose-600">FE</th>
                  <th className="px-1 py-1.5 text-center font-bold min-w-[28px] text-amber-600">HEX</th>
                  <th className="px-1 py-1.5 text-center font-bold min-w-[32px] text-red-500">Atr</th>
                  <th className="px-1 py-1.5 text-center font-bold min-w-[22px] text-slate-500" title="X=No ingresado, Q=Finiquito">X/Q</th>
                  <th className="px-1 py-1.5 text-center font-bold min-w-[38px] bg-primary/5" title="D.Trab = 30 - X/Q - P - F - L - T - equiv atraso">D.Trab</th>
                </tr>
              </thead>
              <tbody>
                {dForRender.grid.length === 0 ? (
                  <tr><td colSpan={d.days_in_month + 14} className="text-center py-12 text-muted-foreground">
                    <CalendarDays className="h-8 w-8 mx-auto mb-2 opacity-40" />
                    {filtroTrabajadores.length > 0 ? 'Ningún trabajador del filtro tiene datos en este mes' : `Sin registros de HH para ${mesDisplayLabel(mes)}`}
                  </td></tr>
                ) : dForRender.grid.map((row, ri) => (
                  <tr key={ri} className="border-t hover:bg-muted/20 transition-colors" data-testid={`row-${row.trabajador_numero}`}>
                    <td className="sticky left-0 bg-background/95 backdrop-blur z-10 px-2 py-1 font-medium border-r truncate max-w-[130px]" title={row.nombre}>
                      {row.nombre}
                    </td>
                    <td className="px-1 py-1 text-center tabular-nums text-muted-foreground">{row.ficha}</td>
                    <td className="px-1 py-1 text-muted-foreground text-[10px]">{row.rut}</td>
                    <td className="px-1 py-1 text-muted-foreground truncate max-w-[75px] border-r" title={row.cargo}>{row.cargo}</td>
                    {row.cells.map((cell) => {
                      const weekendBg = cell.is_feriado ? 'bg-rose-50/60 dark:bg-rose-900/10'
                        : cell.is_sun ? 'bg-red-50/50 dark:bg-red-900/10'
                        : cell.is_sat ? 'bg-amber-50/50 dark:bg-amber-900/10' : '';
                      return (
                        <td key={cell.dia} className={`px-0 py-0.5 text-center ${weekendBg}`}>
                          <CellEditor cell={cell} onSave={(tipo) => handleClasificar(row.trabajador_numero, cell.dia, tipo)} />
                        </td>
                      );
                    })}
                    <td className="px-1 py-1 text-center font-bold border-l tabular-nums">{row.total_presente}</td>
                    <td className="px-1 py-1 text-center tabular-nums text-blue-600">{row.ausencias?.P || ''}</td>
                    <td className="px-1 py-1 text-center tabular-nums text-purple-600">{row.ausencias?.L || ''}</td>
                    <td className="px-1 py-1 text-center tabular-nums text-red-600">{row.ausencias?.F || ''}</td>
                    <td className="px-1 py-1 text-center tabular-nums text-cyan-600">{row.dias_vacaciones || ''}</td>
                    <td className="px-1 py-1 text-center tabular-nums text-yellow-700">{row.ausencias?.T || ''}</td>
                    <td className="px-1 py-1 text-center tabular-nums text-rose-600">{row.ausencias?.FE || ''}</td>
                    <td className="px-1 py-1 text-center font-bold tabular-nums text-amber-600">{row.total_hex || ''}</td>
                    <td className="px-1 py-1 text-center tabular-nums text-red-500 text-[10px]"
                      title={row.total_atraso_min ? `${row.total_atraso_min}min = ${row.equiv_atraso}d equiv, ${row.dias_con_atraso} día(s)` : ''}>
                      {row.total_atraso_min ? `${row.total_atraso_min}m` : ''}
                      {row.equiv_atraso > 0 && <span className="block text-[8px] opacity-70">{row.equiv_atraso}d</span>}
                    </td>
                    <td className="px-1 py-1 text-center tabular-nums text-slate-500">{row.dias_x || ''}</td>
                    <td className="px-1 py-1 text-center font-bold tabular-nums bg-primary/5">{row.dias_trabajados}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-2.5 text-xs text-muted-foreground">
        <span className="flex items-center gap-1"><span className={`inline-block w-5 h-5 leading-5 rounded text-center text-[10px] font-bold ${CELL_STYLES.A}`}>A</span> Presente</span>
        <span className="flex items-center gap-1"><span className="inline-block w-5 h-5 leading-5 rounded text-center text-[10px] font-bold ring-2 ring-orange-500 bg-muted/40 text-muted-foreground">*</span> Sin info (laborable)</span>
        <span className="flex items-center gap-1"><span className={`inline-block w-5 h-5 leading-5 rounded text-center text-[10px] font-bold ${CELL_STYLES['*']}`}>*</span> Fds</span>
        <span className="flex items-center gap-1"><span className={`inline-block w-5 h-5 leading-5 rounded text-center text-[10px] font-bold ${CELL_STYLES.X}`}>X</span> Pre-ingreso</span>
        {Object.entries(TIPO_LABELS).map(([k, v]) => (
          <span key={k} className="flex items-center gap-1">
            <span className={`inline-block w-5 h-5 leading-5 rounded text-center text-[10px] font-bold ${CELL_STYLES[k]}`}>{k}</span> {v}
          </span>
        ))}
        <span className="mx-0.5">|</span>
        <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 bg-amber-400 rounded-full" /> HEX</span>
        <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 bg-red-400 rounded-full" /> Atraso</span>
        <span className="flex items-center gap-1"><span className="inline-block w-4 h-3 bg-amber-100 dark:bg-amber-900/30 rounded" /> Sáb</span>
        <span className="flex items-center gap-1"><span className="inline-block w-4 h-3 bg-red-100 dark:bg-red-900/30 rounded" /> Dom</span>
        <span className="flex items-center gap-1"><span className="inline-block w-4 h-3 bg-rose-200 dark:bg-rose-800/40 rounded" /> Feriado</span>
      </div>
      <p className="text-[10px] text-muted-foreground">
        D.Trab = 30 - X/Q - P - F - L - T - (min atraso / 540)
      </p>
    </div>
  );
}
